/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-29 10:22:56
 * @ Modified by: David Helmick
 * @ Modified time: 2025-09-03 15:17:55
 * @ Description: Determines which page to show based off of url
 */

import React, { Suspense, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { AnimatePresence, motion } from 'framer-motion'

import {
  StyledEngineProvider,
} from '@mui/material/styles'

import AppThemeProvider from './theme/AppThemeProvider'
import {
  includes,
  isNil,
  filter,
  merge,
  cloneDeep,
  isEmpty,
  get,
} from './utils/lodash'

import withSession from '@/housefolios-components/Session/withSession'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import SuspenseLoading from '@/housefolios-components/Routes/SuspenseLoading'
import {
  Tour,
  AnalyzeAreaTour,
  AnalyzeCompsTour,
  ChooseStrategyTour,
  FeedbackTour,
  TakeActionTour,
  VerifyFinancialsTour,
  useTour,
} from '@/housefolios-components/Tours'
import SubDomain from '@/housefolios-components/Routes/SubDomain'
import useAffiliate from './hooks/useAffiliate'

import { Helmet } from 'react-helmet-async'
import { PropertySearchProvider } from '@/context/PropertySearchContext'
import SignedIn from './housefolios-components/Routes/SignedIn'
import NotSignedIn from './housefolios-components/Routes/NotSignedIn'
import {
  isDelinquentOrg,
  resolveMarketplaceSubdomain,
} from './utils/marketplaceSubdomain'

const RoutesComponent = (props) => {
  const {
    openAnalyzeAreaTour,
    openAnalyzeCompsTour,
    openChooseStrategyTour,
    openFeedbackTour,
    openTakeActionTour,
    openTour,
    openVerifyFinancialsTour,
    setOpenAnalyzeAreaTour,
    setOpenAnalyzeCompsTour,
    setOpenChooseStrategyTour,
    setOpenFeedbackTour,
    setOpenTakeActionTour,
    setOpenTour,
    setOpenVerifyFinancialsTour,
  } = useTour()

  const location = useLocation()

  const { session, refetch } = props
  const { me } = session || {}
  const isLoggedIn = !isNil(me) ? true : false

  const hostname = import.meta.env.VITE_HOSTNAME

  const subdomain = resolveMarketplaceSubdomain({
    host: window.location.host,
    search: window.location.search,
    envHostname: hostname,
  })

  const isNotPropertyDashboard =
    !includes(window.location.href, 'property-details') &&
    !includes(window.location.href, 'editLot')

  // var superAdmin = false
  // if (isLoggedIn)
  //   superAdmin =
  //     filter(me.roles, (role) => role.role[0] === 'superAdmin')
  //       .length > 0
  //       ? true
  //       : false

  // var deactiveChild = false
  // if (isLoggedIn)
  //   if (me.activeOrg)
  //     if (me.activeOrg.parentCorpDetails)
  //       if (!me.activeOrg.parentCorpDetails.active)
  //         deactiveChild = true

  const delinquent = isLoggedIn && isDelinquentOrg(me?.activeOrg)

  var noOrg = false
  if (isLoggedIn)
    if (
      filter(me.roles, (role) => {
        return role.role[0] !== 'deactive'
      }).length === 0
    )
      noOrg = true

  var defaultPage = '/launchpad'
  if (isLoggedIn) {
    const preferences = merge(
      cloneDeep(DEFAULT_PREFERENCES),
      props?.session?.me?.activeOrg?.memberPreferences,
      props?.session?.me?.activeOrg?.preferences,
      props.session.me.preferences,
    )
    const { general = {} } = preferences
    if (general.defaultPage) defaultPage = general.defaultPage.value
  }

  const [
    { affiliate, affiliateLoading, urlAffiliate, urlHelmetData },
  ] = useAffiliate({
    memberValue: isLoggedIn
      ? props?.session?.me?.activeOrg?.member
      : null,
  })

  // const favicon = document.getElementById('favicon')
  // const title = document.getElementById('titleId')
  let sidebarMemberSmallImage = '/images/LeftNavLogo.png'
  let titleText = 'Housefolios - Analyze, Manage and Sell Real Estate'
  if (urlAffiliate) {
    if (!isEmpty(urlHelmetData)) {
      sidebarMemberSmallImage =
        urlHelmetData.icon || '/images/LeftNavLogo.png'
      titleText = urlHelmetData.titleText || titleText
    }
    // actually affiliate takes priority
    if (!isEmpty(affiliate)) {
      const { sidebar = {} } = affiliate
      sidebarMemberSmallImage =
        get(sidebar, 'sidebarMemberSmallImage.0.url') ||
        '/images/LeftNavLogo.png'
      titleText = affiliate?.companyName || titleText
    }
  } else {
    if (isLoggedIn && affiliate) {
      if (!isEmpty(affiliate)) {
        const { sidebar = {} } = affiliate
        sidebarMemberSmallImage =
          get(sidebar, 'sidebarMemberSmallImage.0.url') ||
          '/images/LeftNavLogo.png'
        titleText = affiliate?.companyName || titleText
      }
    }
  }

  // favicon.href = sidebarMemberSmallImage
  // title.innerText = titleText

  // var salesperson = false
  // if (isLoggedIn) {
  //   const includesOrg = find(me.roles, [
  //     'organization._id',
  //     me.activeOrg._id,
  //   ])

  //   salesperson = includesOrg ? includesOrg.role[0] === 'salesperson' : false
  // }

  const [isCollapsed, setIsCollapsed] = useState(
    window.innerWidth >= 950 && window.innerWidth < 1200,
  )

  const updateScreenSize = () => {
    setIsCollapsed(
      window.innerWidth >= 950 && window.innerWidth < 1200,
    )
  }

  useEffect(() => {
    window.addEventListener('resize', updateScreenSize)
    return () =>
      window.removeEventListener('resize', updateScreenSize)
  })

  const navigate = useNavigate()

  //SPECIFIC COMMENT: render prop should be used for when routes are using functions instead of just components
  //Ex: component={Component} render={(props) => <Component />}

  // if (isLoggedIn) {
  //   if (noOrg || !me.activeOrg) {
  //     navigate('/deactivated')
  //   } else if (delinquent) {
  //     navigate('/delinquent')
  //     // } else if (includes(
  //     //   window.location.search,
  //     //   'sharedPortfolioToken=',
  //     // )) {
  //     //   navigate('/sharedPortfolio' + window.location.search)
  //     // } else if (includes(
  //     //   window.location.search,
  //     //   'sharedMarketplaceToken=',
  //     // )) {
  //     //   navigate('/sharedMarketplace' + window.location.search)
  //     // } else if (includes(
  //     //   window.location.search,
  //     //   'sharedPropertyToken=',
  //     // )) {
  //     //   navigate('/sharedProperty' + window.location.search)
  //     // } else if (includes(
  //     //   window.location.search,
  //     //   'sharedPropertiesToken=',
  //     // )) {
  //     //   navigate('/sharedProperties' + window.location.search)
  //   }
  // }

  return (
    <PropertySearchProvider session={props.session}>
      <div>
        {subdomain ? (
          <SubDomain subdomain={subdomain} />
        ) : (
          <StyledEngineProvider injectFirst>
            <Helmet>
              <link
                id="favicon"
                rel="shortcut icon"
                href={sidebarMemberSmallImage}
                async
              />
              <title id="titleId">{titleText}</title>
            </Helmet>
            <AppThemeProvider
                variant={
                  isNotPropertyDashboard ? 'default' : 'dashboard'
                }
              >
              <AnimatePresence>
                <Suspense fallback={<SuspenseLoading />}>
                  {isLoggedIn ? (
                    <SignedIn
                      me={me}
                      affiliate={affiliate}
                      noOrg={noOrg}
                      delinquent={delinquent}
                      defaultPage={defaultPage}
                      setOpenAnalyzeAreaTour={setOpenAnalyzeAreaTour}
                      setOpenAnalyzeCompsTour={
                        setOpenAnalyzeCompsTour
                      }
                      setOpenChooseStrategyTour={
                        setOpenChooseStrategyTour
                      }
                      setOpenFeedbackTour={setOpenFeedbackTour}
                      setOpenTakeActionTour={setOpenTakeActionTour}
                      setOpenTour={setOpenTour}
                      setOpenVerifyFinancialsTour={
                        setOpenVerifyFinancialsTour
                      }
                    />
                  ) : (
                    <NotSignedIn
                      refetch={refetch}
                      affiliate={affiliate}
                      setOpenAnalyzeAreaTour={setOpenAnalyzeAreaTour}
                      setOpenAnalyzeCompsTour={
                        setOpenAnalyzeCompsTour
                      }
                      setOpenChooseStrategyTour={
                        setOpenChooseStrategyTour
                      }
                      setOpenFeedbackTour={setOpenFeedbackTour}
                      setOpenTakeActionTour={setOpenTakeActionTour}
                      setOpenTour={setOpenTour}
                      setOpenVerifyFinancialsTour={
                        setOpenVerifyFinancialsTour
                      }
                    />
                  )}
                </Suspense>
              </AnimatePresence>
              <Tour
                isOpen={openTour}
                closeTour={() => setOpenTour(false)}
                location={location}
              />
              <VerifyFinancialsTour
                isOpenVerifyFinancialsTour={openVerifyFinancialsTour}
                closeVerifyFinancialsTour={() =>
                  setOpenVerifyFinancialsTour(false)
                }
              />
              <AnalyzeCompsTour
                isOpenAnalyzeCompsTour={openAnalyzeCompsTour}
                closeAnalyzeCompsTour={() =>
                  setOpenAnalyzeCompsTour(false)
                }
              />
              <ChooseStrategyTour
                isOpenChooseStrategyTour={openChooseStrategyTour}
                closeChooseStrategyTour={() =>
                  setOpenChooseStrategyTour(false)
                }
              />
              <AnalyzeAreaTour
                isOpenAnalyzeAreaTour={openAnalyzeAreaTour}
                closeAnalyzeAreaTour={() =>
                  setOpenAnalyzeAreaTour(false)
                }
              />
              <TakeActionTour
                isOpenTakeActionTour={openTakeActionTour}
                closeTakeActionTour={() => {
                  setOpenTakeActionTour(false)
                }}
              />
              <FeedbackTour
                isOpenFeedbackTour={openFeedbackTour}
                closeFeedbackTour={() => setOpenFeedbackTour(false)}
              />
              </AppThemeProvider>
          </StyledEngineProvider>
        )}
      </div>
    </PropertySearchProvider>
  )
}

export default withSession(RoutesComponent)
